import { ClockCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { Form, Input, Radio } from 'antd';
import { SettingSwitch } from '@/components/profile/settingCards';
import {
  EMPLOYEE_WORK_TYPE_OPTIONS,
  WORK_TYPE_VALUES,
} from '@/shared/employee';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

// Full time is 40 hours a week (8 a day, 5 days).
const FULL_TIME_WEEK_HOURS = 40;

/**
 * The "Working time" tile of the employee profile: the share of full time and
 * the weekly hours it means on top, the choice and the shift-work switch
 * below. Same fields, rules and save as EmployeeWorkTimeFields, whose work-
 * time card steps aside on the profile.
 */
export default function EmployeeWorkTimeProfilePanel() {
  const {
    COMPANY_DATA,
    form,
    readOnly,
    shiftWorkerKeys,
    shiftWorkerState,
    t,
    workTimeCustomTypeKeys,
    workTimeCustomTypeState,
    workTimeTypeKeys,
    workTimeTypeState,
  } = useEmployeeGeneralDataForm();
  const f = (key, opts) => t(key, { ns: 'forms', ...opts });
  const tp = (key, opts) =>
    t(`employeeProfile.workTimeUi.${key}`, { ns: 'translations', ...opts });

  // The choice is not mounted while viewing, so read the stored values.
  const workTimeType = Form.useWatch(workTimeTypeKeys, {
    form,
    preserve: true,
  });
  const workTimeCustomType = Form.useWatch(workTimeCustomTypeKeys, {
    form,
    preserve: true,
  });
  const isCustom = !WORK_TYPE_VALUES[workTimeType];
  const percent = isCustom
    ? Number(workTimeCustomType) || 0
    : WORK_TYPE_VALUES[workTimeType];
  const weekHours =
    Math.round(((FULL_TIME_WEEK_HOURS * percent) / 100) * 10) / 10;
  const optionLabel = (value) =>
    f(value, {
      value:
        value === 'customTime'
          ? (workTimeCustomType ?? 0)
          : (WORK_TYPE_VALUES[value] ?? 0),
    });

  return (
    <div className="grid max-w-[620px] gap-4">
      <section className="flex items-center gap-3 rounded-xl border border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.08)] p-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.14)] text-[18px] text-accent">
          <ClockCircleOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[24px] font-extrabold leading-none text-accent">
            {percent}%
          </div>
          <div className="mt-1 text-[12px] text-zinc-600">
            {tp('weekHours', { hours: weekHours })}
          </div>
        </div>
      </section>

      {/* Kept mounted while viewing (only hidden): an unregistered choice
          reads as changed and would offer the effective date of a change. */}
      {workTimeTypeState?.visible && (
        <Form.Item
          name={workTimeTypeKeys}
          label={f('workType')}
          className="!mb-0"
          hidden={readOnly}
        >
          <Radio.Group
            className="grid gap-2 [&_label]:items-start [&_label]:justify-start"
            disabled={workTimeTypeState?.disabled}
          >
            {EMPLOYEE_WORK_TYPE_OPTIONS.map(({ key, value }) => (
              <Radio
                key={key}
                value={value}
                className="setting-card mr-0 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 hover:border-accent"
              >
                {optionLabel(value)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      )}

      {isCustom && workTimeCustomTypeState?.visible && (
        <Form.Item
          name={workTimeCustomTypeKeys}
          label={f('workPercentage')}
          className="!mb-0 max-w-[240px]"
          hidden={readOnly}
          rules={[
            {
              required: true,
              message: t('formValidation.workPercentageRequired', {
                ns: 'forms',
              }),
            },
          ]}
        >
          <Input
            min={0}
            max={100}
            step="0.01"
            type="number"
            placeholder={f('workPercentage')}
            prefix={<span className="mr-1 border-r pr-2.5">%</span>}
            disabled={workTimeCustomTypeState?.disabled}
            onChange={(e) =>
              formValidateAndSetNumber(form, e, workTimeCustomTypeKeys, true)
            }
          />
        </Form.Item>
      )}

      {COMPANY_DATA.applicationSettings?.weekendLeaves && (
        <div className="grid grid-cols-2 gap-2">
          <Form.Item name={shiftWorkerKeys} noStyle>
            <SettingSwitch
              locked={readOnly || shiftWorkerState?.disabled}
              icon={<SyncOutlined />}
              title={f('shiftWorker')}
              wide
            />
          </Form.Item>
        </div>
      )}
    </div>
  );
}
